import { useTheme } from '@/hooks/useTheme';

interface CTASectionProps {
  onBookAudit: () => void;
}

export default function CTASection({ onBookAudit }: CTASectionProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section
      id="cta"
      className="relative overflow-hidden transition-colors duration-500"
      style={{ background: isDark ? '#0E1116' : '#ffffff' }}
    >
      {/* Full-bleed background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/5719d6083c611ad71a360ff70627e102.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
          style={{ opacity: isDark ? 1 : 0.55 }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: isDark ? 'rgba(14,17,22,0.75)' : 'rgba(245,245,247,0.48)' }}></div>
        <div
          className="absolute inset-0"
          style={{
            background: isDark
              ? 'radial-gradient(ellipse at 50% 60%, rgba(34,211,238,0.08) 0%, transparent 60%)'
              : 'radial-gradient(ellipse at 50% 60%, rgba(0,113,227,0.06) 0%, transparent 60%)'
          }}
        />
      </div>

      {/* Top border */}
      <div className={`absolute top-0 left-0 right-0 h-px ${
        isDark ? 'bg-gradient-to-r from-transparent via-white/10 to-transparent' : 'bg-gradient-to-r from-transparent via-black/10 to-transparent'
      }`}></div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 py-36 text-center flex flex-col items-center">
        {/* Badge */}
        <div className={`inline-flex items-center gap-2 px-5 py-2 rounded-full border backdrop-blur-sm mb-10 ${
          isDark ? 'border-white/10 bg-white/5' : 'border-black/10 bg-white/60'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full inline-block animate-pulse ${isDark ? 'bg-cyan-400' : 'bg-blue-500'}`}></span>
          <span className={`text-xs tracking-[0.15em] uppercase font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Start Now</span>
        </div>

        {/* Headline */}
        <h2 className={`text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight mb-6 ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
          Bring <span className="text-gradient-cyan">Structure</span>
          <br />
          to Your Operations
        </h2>

        {/* Sub */}
        <p className={`text-xl leading-relaxed max-w-xl mb-12 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Discover how intelligent systems can transform the way your business runs—starting with a single audit.
        </p>

        {/* CTA */}
        <button
          onClick={onBookAudit}
          className={`cursor-pointer whitespace-nowrap inline-flex items-center gap-3 px-10 py-5 rounded-full text-base font-semibold transition-all duration-200 tracking-wide ${
            isDark
              ? 'bg-white text-[#0E1116] hover:bg-gray-100'
              : 'bg-[#0E1116] text-white hover:bg-gray-800'
          }`}
        >
          <i className="ri-calendar-check-line text-lg"></i>
          Schedule Your System Audit
        </button>

        {/* Footnote */}
        <p className={`mt-8 text-sm tracking-wide ${isDark ? 'text-gray-600' : 'text-gray-500'}`}>
          No commitment required — we&apos;ll assess your operations and outline a clear path forward.
        </p>
      </div>
    </section>
  );
}